import { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PawPrint, CheckCircle, Clock, MapPin, MessageCircle, Heart, Stethoscope, ChevronRight } from "lucide-react";
import DonationFlow from "@/components/DonationFlow";
import PublicNav from "@/components/PublicNav";
import { trackConversion } from "@/lib/analytics";
import { useT } from "@/lib/translations";

export default function ApplicationSuccessPage() {
  const t = useT();
  const { applicationId } = useParams();
  const location = useLocation();

  const [dogId, setDogId] = useState(location.state?.dogId || null);
  const [dogName, setDogName] = useState(location.state?.dogName || null);
  const [showDonation, setShowDonation] = useState(false);
  const [donationType, setDonationType] = useState("vet");
  const [supportDone, setSupportDone] = useState(false);

  // Fire adoption application conversion event once on mount
  useEffect(() => {
    if (applicationId && applicationId !== "test123") {
      trackConversion("adoption_completed", {
        application_id: applicationId,
        dog_id: dogId || undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  useEffect(() => {
    if (!dogId && applicationId && applicationId !== "test123") {
      axios.get(`${API}/pativista/applications/${applicationId}`)
        .then(res => {
          const app = res.data;
          if (app?.dog_id) setDogId(app.dog_id);
          if (app?.dog_name) setDogName(app.dog_name);
        })
        .catch(() => {});
    }
  }, [dogId, applicationId]);

  const displayName = dogName || t("can dostunun");

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <PublicNav />

      <div className="max-w-2xl mx-auto px-4 md:px-8 py-12">
        <Card className="bg-card border border-border/50 shadow-lg" data-testid="success-card">
          <CardContent className="p-6 md:p-10 space-y-5">

            {/* 1) Başlık + tik */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl" data-testid="success-title">{t("Başvurun alındı!")}</h1>
            </div>

            {/* Support success banner */}
            {supportDone && (
              <div className="flex items-center justify-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl" data-testid="support-done-banner">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-700 font-medium">{t("İyi ki varsın! Desteğin alındı.")}</p>
              </div>
            )}

            {/* 2) Above-the-fold CTA */}
            {dogId && (
              <div className="p-5 bg-primary/5 border border-primary/20 rounded-2xl space-y-4" data-testid="aninda-destek-block">
                <div className="flex items-start gap-3">
                  <Stethoscope className="w-6 h-6 text-primary mt-0.5 shrink-0" />
                  <div>
                    <h2 className="font-heading text-lg leading-snug">
                      {t("Biz başvurunu değerlendirirken, {name}'nı yalnız bırakma.").replace("{name}", displayName)}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1.5">
                      {t("İstersen bugün küçük bir destekle {name}'nın veteriner masrafına katkı olabilirsin. Bu destek doğrudan fark yaratır.").replace(/\{name\}/g, displayName)}
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => { setDonationType("vet"); setShowDonation(true); }}
                  className="w-full btn-pill bg-primary text-primary-foreground h-12 text-base font-semibold shadow-md shadow-primary/20 transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30"
                  data-testid="thankyou-vet-btn"
                >
                  {t("Veteriner Desteği Ol")} <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">{t("İstersen daha küçük bir katkı da seçebilirsin.")}</p>

                <button
                  onClick={() => { setDonationType("mama"); setShowDonation(true); }}
                  className="w-full text-center text-sm font-medium text-primary/80 py-2 rounded-full transition-all duration-200 hover:text-primary hover:-translate-y-0.5 hover:bg-primary/5"
                  data-testid="thankyou-mama-btn"
                >
                  {t("Mama Desteği vermek istiyorum")}
                </button>
              </div>
            )}

            {/* 3) Bilgi kutuları */}
            <div className="space-y-3 text-left">
              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-xl">
                <Clock className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t("Genellikle <strong>24 saat</strong> içinde dönüş yapıyoruz; yoğunlukta <strong>1 haftaya</strong> uzayabilir.") }} />
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-xl">
                <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground"><strong>{t("Randevusuz ziyaret kabul edilmez.")}</strong></p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-xl">
                <MessageCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">{t("Seçtiğiniz kanaldan (WhatsApp/Instagram) yazacağız.")}</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/10 rounded-xl" data-testid="success-333-block">
                <Heart className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t("<strong>Uyum dönemi normaldir:</strong> İlk 3 gün zorlayıcı olabilir; ilk 3 haftada rutin oturur; ilk 3 ayda tam uyum gerçekleşir. Bu süreçte destek veririz.") }} />
              </div>
            </div>

            {/* 4) Başvuru no */}
            {applicationId && (
              <p className="text-xs text-center text-muted-foreground">
                {t("Başvuru No:")} <span className="font-mono">{applicationId}</span>
              </p>
            )}

            {/* 5) Alt butonlar */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Link to="/canlar" className="flex-1" data-testid="success-browse-dogs-btn">
                <Button className="btn-pill bg-primary text-primary-foreground w-full font-semibold shadow-md shadow-primary/20 transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30">
                  <PawPrint className="w-4 h-4 mr-2" /> {t("Diğer Canları Gör")}
                </Button>
              </Link>
              <Link to="/" className="flex-1" data-testid="success-home-btn">
                <Button variant="outline" className="btn-pill w-full border-2 border-primary/40 bg-white text-primary font-semibold transition-all duration-200 hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30">{t("Ana Sayfaya Dön")}</Button>
              </Link>
            </div>

          </CardContent>
        </Card>
      </div>

      {dogId && (
        <DonationFlow
          open={showDonation}
          onClose={() => { setShowDonation(false); setSupportDone(true); }}
          dogId={dogId}
          dogName={displayName}
          type={donationType}
          upsellVariant={donationType === "mama" ? "browse" : "meal"}
          onUpsellMeal={() => {
            setShowDonation(false);
            setDonationType("mama");
            setTimeout(() => setShowDonation(true), 250);
          }}
        />
      )}
    </div>
  );
}
